import _ from "lodash";
import React, {
} from "react";
import {
  bindActionCreators,
} from "redux";
import querystring from "query-string";

import {
  asyncOperation,
  asyncDebounce,
  inline,
  createUiListSelector,
  derived,
} from "CommonState/action-helpers";
import apifetch from "CommonCore/apifetch";
import store, {
  applyStore,
  deferredExecute,
} from "CommonState/store";
import {
  redirect,
} from "CommonCore/router-util";
import {
  makeGetId,
} from "CommonCore/schema-util";


const apiCache = {};
const tableWatchers = {};
export const registeredRoutes = [];

export default function api(desc) {
  return apiCache[desc.name] || (apiCache[desc.name] = createApi(desc));
}

export function view(desc) {
  return apiCache[desc.name] || (apiCache[desc.name] = createView(desc));
}

export function notifyChanged({ t, id }) {
  return dispatch => {
    const watcherPromises = _.map(tableWatchers[t], w => dispatch(w(id)));
    return Promise.all(watcherPromises);
  };
}

function createApi(desc) {
  const capped = _.upperFirst(desc.name);
  const listName = `${ desc.name }List`;
  const listItemCountName = `${ listName }ItemCount`;
  const lookupName = `${ desc.name }Lookup`;
  const inflightName = `${ listName }Inflight`;
  const currentName = `current${ capped }`;
  const currentInflightName = `${ currentName }Inflight`;
  const saveActionName = `save${ capped }`;
  const deleteActionName = `delete${ capped }`;
  const clearListName = `clear${ capped }List`;
  const updateEntityName = `update${ capped }InStore`;
  const refreshEntityName = `refresh${ capped }InStore`;
  const retrieveListName = `retrieve${ capped }List`;
  const endpointArgsName = `${ desc.name }EndpointArgs`;
  const endpointResultsCacheName = `${ desc.name }EndpointResultsCache`;
  const endpointInflightCacheKey = `${ listName }InflightCache`;
  const preloadCurrentEntityKey = `${ desc.name }PreloadCurrent`;
  const preloadCurrentInflightKey = `${ desc.name }PreloadCurrentInflight`;
  const uiListName = `ui${ capped }List`;

  const getId = makeGetId(desc.id);

  if (desc.schema) {
    desc.schema.id = getId;
  }

  const currentSelector = derived(
    currentName,
    state => state.locationParams && state.locationParams[currentName],
    state => _.get(state, lookupName),
    state => state[preloadCurrentEntityKey],
    (id, lookup) => id && lookup && lookup[id],
  );

  const currentSelectorNoList = derived(
    currentName,
    state => state.locationParams && state.locationParams[currentName],
    state => _.get(state, lookupName),
    state => state[preloadCurrentEntityKey],
    (id, lookup, preloadedCurrent) => id && (lookup ? (lookup[id] || Error()) : preloadedCurrent),
  );

  const uiListSelector = !desc.noUiList && createUiListSelector(listName, desc.schema);

  function clearList() {
    return inline({
      [listName]: "discard",
      [lookupName]: "discard",
      [endpointResultsCacheName]: "discard",
    }, _.noop);
  }

  function retrieveList(paramStr) {
    return asyncOperation(listName, async function(dispatch, getState) {
      const url = desc.endpoint + (paramStr ? "?" + paramStr : "");
      const opts = { responseHeaders: {} };
      let blob = {};
      try {
        blob = await apifetch(url, opts);
      } catch(e) {
        // PBP-2281 alles >= 400 als leeres Array betrachten und nicht ständig neuladen
        if (e.status >= 400) {
          blob.data = [];
        } else {
          throw e;
        }
      }

      let list = blob.data;

      // Schema-Description enthält async Operation nachdem die Liste geladen wurde
      if (desc.afterRetrieveList && _.isFunction(desc.afterRetrieveList)) {
        list = await desc.afterRetrieveList(list, getState());
      }

      if (!paramStr || desc.serverSideSearch) {
        dispatch(inline(
          {
            [listName]: "discard",
            [lookupName]: "discard",
          },
          state => {
            _.set(state, listName, list);
            if (!desc.noLookup) {
              _.set(state, lookupName, _.keyBy(list, getId));
            }
            if (desc.serverSideSearch) {
              const rows = +opts.responseHeaders["x-rows"];
              _.set(state, listItemCountName, isNaN(rows) ? 0 : rows);
            }
          },
        ));
        return list;
      }
      dispatch(inline(
        {
          [listName]: "shallow",
          [lookupName]: "discard",
          [endpointResultsCacheName]: "shallow",
        },
        state => {
          const mergedList = _.unionBy(_.get(state, listName), list, getId);
          _.set(state, listName, mergedList);
          if (!desc.noLookup) {
            _.set(state, lookupName, _.keyBy(mergedList, getId));
          }
          _.set(state, [endpointResultsCacheName, paramStr], true);
        },
      ));
    }, paramStr);
  }

  function ensureList(params) {
    return async function(dispatch, getState) {
      if (desc.serverSideSearch) {
        return;
      }

      const state = getState();
      const paramStr = params ? querystring.stringify(params) : void 0;

      const hasList = state[listName];
      const listInflight = state[inflightName];
      const listPartlyInflight = _.get(state, [endpointInflightCacheKey, paramStr]);
      const hasResultInCache = _.get(state, [endpointResultsCacheName, paramStr]);

      // PBP-1804 sicherstellen, dass Liste erst mit gegebenen params geladen wird
      if (desc.endpointParams && _.isEmpty(params)) {
        return;
      }

      if ((!hasList && !listInflight) || (params && !hasResultInCache && !listPartlyInflight)) {
        await dispatch(retrieveList(paramStr));
      }
    };
  }

  function preloadCurrent(id) {
    return async(dispatch, getState) => {
      const state = getState();
      if (!id || state[preloadCurrentInflightKey] === id || state[listName] || state[preloadCurrentEntityKey]) {
        return;
      }

      dispatch(inline({ [preloadCurrentInflightKey]: "discard" }, state => state[preloadCurrentInflightKey] = id));

      const endpoint = `${ desc.endpoint }/${ id }`;
      let entity;
      try {
        entity = await apifetch(endpoint);
        if (entity.data && _.isArray(entity.data)) {
          entity = entity.data[0];
        }
      } finally {
        dispatch(inline({ [preloadCurrentInflightKey]: "discard", [preloadCurrentEntityKey]: "discard" }, state => {
          state[preloadCurrentInflightKey] = undefined;
          state[preloadCurrentEntityKey] = entity;
        }));
      }
    };
  }

  function updateEntityInStore(id, entity) {
    return function(dispatch) {
      let result;
      dispatch(inline({
        [listName]: "shallow",
        [lookupName]: "shallow",
      }, state => {
        const list = state[listName] || (state[listName] = []);
        const i = _.findIndex(list, e => getId(e) === id);
        if (i !== -1) {
          if (entity) {
            list[i] = entity;
          } else {
            list.splice(i, 1);
          }
        } else if (entity) {
          list.push(entity);
        }

        if (!desc.noLookup) {
          const lookup = state[lookupName] || (state[lookupName] = {});
          const oldEntity = lookup[id];
          if (entity) {
            lookup[id] = entity;
          } else {
            delete lookup[id];
          }
          result = entity || oldEntity;
        }
      }));

      return result;
    };
  }

  function updateEntitiesInStore(entities) {
    return function(dispatch) {
      dispatch(inline({
        [listName]: "shallow",
        [lookupName]: "shallow",
      }, state => {
        state[listName] = _.unionBy(entities, state[listName], getId);
        if (!desc.noLookup) {
          const lookup = state[lookupName] || (state[lookupName] = {});
          for (const e of entities) {
            lookup[getId(e)] = e;
          }
        }
      }));
    };
  }

  function saveEntity(entity) {
    return asyncOperation(currentName, async function(dispatch) {
      let id = getId(entity);
      const method = id ? "PUT" : "POST";
      let endpoint = desc.endpoint;
      if (id) {
        endpoint = `${ endpoint }/${ id }`;
      }

      let newEntity = await apifetch(endpoint, {
        method,
        body: JSON.stringify(entity),
      });

      if (_.isEmpty(newEntity)) {
        // no data returned (but no error thrown), use own copy
        newEntity = _.cloneDeep(entity);
      } else {
        id = getId(newEntity);

        const resultKeys = _.keys(newEntity);
        if (_.isString(desc.id) && resultKeys.length === 1 && resultKeys[0] === desc.id) {
          /*
           * The server didn't return the whole object.
           * Clone the sent entity and apply the returned ID to it.
           */
          newEntity = _.cloneDeep(entity);
          newEntity[desc.id] = id;
        }
      }

      const result = dispatch(updateEntityInStore(id, newEntity));

      if (desc.onSaved) {
        await dispatch(desc.onSaved(newEntity));
      }

      return result;
    });
  }

  function refreshEntity(id) {
    return async function(dispatch, getState) {
      if (!id) {
        // Keinen Aufruf ohne ID an Service geben
        return;
      }

      const lookup = getState()[lookupName];
      const oldEntity = lookup && lookup[id];

      let newEntity;
      try {
        newEntity = await apifetch(`${ desc.endpoint }/${ id }`);
        if (newEntity.data && _.isArray(newEntity.data)) {
          newEntity = newEntity.data[0];
        }
      } catch(e) {
        /*
         * An error here means in general that the user
         * can no longer access this entity. Delete entity from store.
         */
      }

      await dispatch(updateEntityInStore(id, newEntity));
      const entityToFormat = newEntity || oldEntity;
      if (entityToFormat && !_.isEqual(oldEntity, newEntity)) {
        const toUserString = desc.schema && desc.schema.toUserString;
        return toUserString && {
          name: toUserString(entityToFormat),
          event: !newEntity ? "deleted" : "modified",
        };
      }
    };
  }

  function deleteEntity(id) {
    return asyncOperation(currentName, async function(dispatch) {
      await apifetch(`${ desc.endpoint }/${ id }`, {
        method: "DELETE",
      });
      return dispatch(updateEntityInStore(id, null));
    });
  }

  function mapDispatch(dispatch) {
    return bindActionCreators(_.extend({
      [saveActionName]: saveEntity,
      [deleteActionName]: deleteEntity,
      [clearListName]: clearList,
      [retrieveListName]: retrieveList,
      [updateEntityName]: updateEntityInStore,
      [refreshEntityName]: refreshEntity,
    }, desc.extraActions), dispatch);
  }

  function noCache(Component) {
    const ActualComponent = Component.WrappedComponent ? Component.WrappedComponent : Component;
    @store
    class CacheEraser extends ActualComponent {
      componentWillUnmount() {
        super.componentWillUnmount && super.componentWillUnmount();
      }

      shouldComponentUpdate() {
        /*
         * PBP-1804 Wechsel von /antrag zu /vertrag löst kein componentWillMount aus
         * darum löschen wir den Cache, wenn sich die aktuelle Url geändert hat
         */
        if (this.state && this.props.location && this.state.lastLocation !== this.props.location.pathname) {
          this.setState({ lastLocation: this.props.location && this.props.location.pathname });
          this.props.dispatch(clearList());
        }
        return true;
      }
    }

    return CacheEraser;
  }

  function enableServerSideSearch(Component) {
    @store(uiListSelector)
    class ServerSideExecutor extends React.Component {
      constructor() {
        super();
        this.state = {};
      }

      componentDidMount() {
        // TODO: avoid having multiple server-side executors
        this.executeServerSideSearchImpl = asyncDebounce(this.props.dispatch);
        this.executeServerSideSearch(this.props);
      }

      componentDidUpdate(prevProps) {
        if (!_.isEqual(this.props[uiListName], prevProps[uiListName])) {
          this.executeServerSideSearch(this.props);
        }
      }

      convertFilter(filter) {
        return _.fromPairs(_.map(filter, f => _.isString(f)
            ? ["q", f]
            : [f.field, f.criteria]));
      }

      convertSort(sort) {
        return _.fromPairs(_.map(sort, ({ key, reverse }) => [key, reverse ? "desc" : "asc"]));
      }

      executeServerSideSearch(props) {
        // erst suchen, wenn die Nutzereinstellungen geladen wurden
        const listData = props[uiListName];
        if (listData.storedItemsPerPage && listData.storedItemsPerPage.isPlaceholder) {
          return;
        }

        this.executeServerSideSearchImpl(async dispatch => {
          const listData = props[uiListName];
          const { oldListData } = this;

          const params = {
            n: listData.itemsPerPage || 10,
            p: listData.page || 0,
            f: this.convertFilter(listData.filter),
            s: this.convertSort(listData.sort),
          };

          if (!_.isEqual(oldListData, params)) {
            this.oldListData = params;
            await dispatch(retrieveList(querystring.stringify(_.mapValues(params, x => JSON.stringify(x)))));
          }
        });
      }

      render() {
        return <Component {...this.props} />;
      }
    }
    return ServerSideExecutor;
  }

  if (desc.refresh) {
    _.keys(desc.refresh).forEach(table => {
      const watchers = tableWatchers[table] || (tableWatchers[table] = []);
      const idMap = desc.refresh[table];
      watchers.push(changedId => async function(dispatch) {
        if (idMap) {
          const mapResult = idMap(changedId);
          const entityId = _.isFunction(mapResult) ? await dispatch(mapResult) : mapResult;
          return await dispatch(refreshEntity(entityId));
        } else if (desc.endpointParams) {
          dispatch(clearList());
        } else {
          try {
            const list = await dispatch(retrieveList());
            if (list) {
              const toUserString = desc.schema && desc.schema.toUserString;
              return toUserString && {
                name: toUserString(list),
                event: "modified",
              };
            }
          } catch(e) {
            // An error here means in general that the user has no access. Ignore.
          }
        }
      });
    });
  }

  const keysForList = [
    listName,
    !desc.noLookup && lookupName,
    inflightName,
    ...(desc.derived || []),
  ];
  const keysForUiList = !desc.noUiList && [
    ...keysForList,
    uiListSelector,
  ];
  const keysForCurrent = [
    inflightName,
    currentInflightName,
    desc.noListFromCurrent ? currentSelectorNoList : currentSelector,
  ];

  function decorate(storeKeys, additionalDecorators) {
    const f = arg => {
      const fullDecorator = paramTable => Component => {
        function mapState(state, props) {
          if (desc.endpointParams) {
            // desc.endpointParams==true werden nur ausgeführt, wenn Parameter übergeben werden. Check hier unnötig
            /*
             *assert(_.isPlainObject(paramTable));
             *if (!_.isPlainObject(paramTable)) {
             *console.warn(`Endpoint ${desc.name} expects URL arguments but got none`);
             *}
             */
            return {
              [endpointArgsName]: _.mapValues(paramTable, f => f(state, props)),
            };
          }
        }

        if (desc.serverSideSearch) {
          Component = enableServerSideSearch(Component);
        }

        return applyDecorators(
          storeKeys,
          mapDispatch,
          mapState,
          undefined,
          _.compact(additionalDecorators),
          _.compact(desc.dependencies),
          Component,
        );
      };

      /*
       * return _.isFunction(arg) ? fullDecorator()(arg) : fullDecorator(arg); -> alt
       * wichtig für den neuen redux, _.isFunction check liefert für wrapped components jetzt immer false
       * wir wollen die args aber nur direkt an fullDecorator übergeben, wenn es endpoint params sind
       */
      return (_.isFunction(arg) || arg.WrappedComponent) ? fullDecorator()(arg) : fullDecorator(arg);
    };

    f.inflightProperty = inflightName;
    return f;
  }

  const currentPreloaderDecorator = deferredExecute(
    props => props.dispatch(preloadCurrent(props.params?.[currentName])),
  );

  const ensureListDecorator = deferredExecute(
    props => props.dispatch(ensureList(props[endpointArgsName])),
    endpointArgsName,
  );

  const theApi = {
    list: decorate(keysForList, [ensureListDecorator]),
    uiList: !desc.noUiList && decorate(keysForUiList, [ensureListDecorator]),
    current: !desc.noLookup && decorate(keysForCurrent, [currentPreloaderDecorator, !desc.noListFromCurrent && ensureListDecorator]),
    actions: decorate(null),
    noCache,

    schema: desc.schema,
    breadcrumbs: desc.breadcrumbs,
    updateInStore: updateEntityInStore,
    updateInStoreBulk: updateEntitiesInStore,

    id: getId,
    names: {
      name: desc.name,
      capped,
      listName,
      listItemCountName,
      uiListName,
      lookupName,
      inflightName,
      currentName,
      currentInflightName,
      saveActionName,
      deleteActionName,
      endpointArgsName,
    },

    allInflightProperties: [inflightName, ..._.map(desc.dependencies, dep => dep.inflightProperty).filter(_.identity)],

    registerRoutes: components => {
      theApi.routes = createRoutes({ base: desc.name, id: currentName, nestedChildren: desc.nestedChildren }, components);
      registeredRoutes.push(theApi.routes);
    },
  };

  return theApi;
}

function createView(desc) {
  const capped = _.upperFirst(desc.name);
  const listName = `${ desc.name }List`;
  const lookupName = `${ desc.name }Lookup`;
  const inflightName = `${ listName }Inflight`;

  const getId = makeGetId(desc.id);
  if (desc.schema) {
    desc.schema.id = getId;
  }

  function mapDispatch() {
    return {};
  }

  function decorate(storeKeys) {
    const f = Component => {
      Component = applyDecorators(storeKeys, mapDispatch, void 0, void 0, desc.dependencies, Component);
      return Component;
    };
    f.inflightProperty = inflightName;
    return f;
  }
  const inputInjects = desc.inject.map(s => _.isFunction(s) ? s : (state => _.get(state, s)));

  const listSelector = desc.generator
    ? derived(listName, inputInjects, desc.generator)
    : (state, props) => props[listName];

  const listLookupSelector = derived(lookupName, [listSelector], list => _.keyBy(list, getId));
  let inflightSelector;
  let uiListSelector;
  if (!desc.noUiList) {
    inflightSelector = derived(
      inflightName,
      desc.inflight,
      (...args) => _.some(args),
    );
    uiListSelector = createUiListSelector(listName, desc.schema, listSelector, inflightSelector);
  }

  const keysForList = [
    listSelector.__property && listSelector,
    !desc.noLookup && listLookupSelector,
    inflightSelector,
  ];
  const keysForUiList = uiListSelector && [
    uiListSelector,
    ...keysForList,
  ];

  return {
    list: decorate(keysForList),
    uiList: keysForUiList && decorate(keysForUiList),
    schema: desc.schema,

    names: {
      name: desc.name,
      capped,
      listName,
      lookupName,
      inflightName,
      uiListName: `ui${ capped }List`,
    },

    allInflightProperties: [inflightName],
  };
}

function applyDecorators(storeKeys, mapDispatch, mapState, mergeProps, preDependencies, postDependencies, component) {
  // PBP-1804 dependencies in pre und post aufgeteilt. Wichtig für die richtige Reihenfolge bei Endpunkten mit Parametern
  _.forEach(preDependencies, d => component = d(component));
  component = applyStore(_.filter(storeKeys), mapDispatch, mapState, mergeProps)(component);
  delete component.displayName;

  _.forEach(postDependencies, d => component = d(component));
  return component;
}

export function createRoutes(navigation, components) {
  return {
    path: navigation.base,
    indexRoute: { component: components.list },
    childRoutes: [
      { path: "neu", component: components.detailEdit },
      {
        path: ":" + (navigation.id || "id"),
        component: components.detailTabs,
        nestedChildren: navigation.nestedChildren,
        indexRoute: redirect("details"),
        childRoutes: [
          {
            path: "details",
            indexRoute: { component: components.detailView },
            childRoutes: [
              { path: "edit", component: components.detailEdit },
            ],
          },
          ...(components.detailExtra || []),
        ],
      },
    ],
  };
}
